import apiCall from ".";

/**
 * The caller's server-resolved delivery authority:
 * `{ boardScope, overseenServices, canManageTeam, isInternal }`.
 *
 * `boardScope` is `all` (ADMIN), `services` (an active manager — the slugs
 * they oversee plus their own cards) or `self`. None of it is derivable from
 * the role string, so the UI consumes this and never re-derives.
 */
async function getDeliveryMe() {
  return await apiCall("get", "marketplace/fulfillment/me");
}

/**
 * Get fulfillment status counts (lightweight — no profile lookups).
 * @param {Object} params - { serviceSlug?, providerId?, dueDateFrom?, dueDateTo? }
 */
async function getFulfillmentCounts(params = {}) {
  return await apiCall("get", "marketplace/fulfillment/counts", null, params);
}

/**
 * Get per-date task counts for a calendar month (lightweight — dots only).
 * Dates are IST calendar days, matching the day query and the urgency chip.
 * Takes the SAME lenses as the task list so the dots and the rows beneath them
 * answer one question; there is no "no-date" key (read that count off the
 * undated list, which is the query that can actually scope it).
 * @param {Object} params - { month (1-12), year, serviceSlug?, providerId?, search?, urgency? }
 * @returns {Object} e.g. { "2026-03-19": { total: 5, open: 3, overdue: 2, fallback: 1 } }
 */
async function getCalendarCounts(params = {}) {
  return await apiCall("get", "marketplace/fulfillment/calendar-counts", null, params);
}

/**
 * Get paginated tasks — admin sees all, others see only their own.
 * `dueDateFrom`/`dueDateTo` are ISO-8601 instants and are validated as dates;
 * send them with an explicit offset (see `deliveryClock.istDayRangeIso`) so a
 * "day" means the IST day the operator clicked. `statusIn` (CSV) asks the
 * server for a multi-status bucket, with its count and pagination.
 * @param {Object} params - { dueDateFrom?, dueDateTo?, noDueDate?, statusIn?, page?, limit?, serviceSlug?, providerId?, search?, urgency? }
 * @param {boolean} isAdmin - if true, use all-tasks; otherwise use my-tasks
 */
async function getDateTasks(params = {}, isAdmin = true) {
  const endpoint = isAdmin
    ? "marketplace/fulfillment/all-tasks"
    : "marketplace/fulfillment/my-tasks";
  return await apiCall("get", endpoint, null, params);
}

/**
 * Whole delivery board in one call — per-status counts, urgency rollups and
 * the first page of every column, from a single server-side $facet aggregate.
 * @param {Object} params - { serviceSlug?, providerId?, search?, urgency?, limit? }
 */
async function getBoard(params = {}) {
  return await apiCall("get", "marketplace/fulfillment/board", null, params);
}

/**
 * Fetch ONE fulfillment card by id (queue-item shape) — deep links and
 * detail freshness. Non-admin staff only get cards they work.
 * @param {string} fulfillmentId
 */
async function getTaskById(fulfillmentId) {
  return await apiCall("get", `marketplace/fulfillment/task/${fulfillmentId}`);
}

/**
 * Users lens — one row per customer with per-service delivery rollups,
 * paginated BY USER (most urgent first). Non-admins get only their users.
 * @param {Object} params - { serviceSlug?, providerId?, search?, urgency?, userSort?, page?, limit? }
 */
async function getUsersOverview(params = {}) {
  return await apiCall("get", "marketplace/fulfillment/users", null, params);
}

/**
 * One kanban column paginated BY USER (the user-sectioned board): a page of
 * users, each with their first `taskLimit` tasks in that status + the true
 * per-user total. Inner task pages continue via getAllTasks(gamePlayerId+status).
 * @param {Object} params - { status, serviceSlug?, providerId?, search?, urgency?, userSort?, page?, limit?, taskLimit? }
 */
async function getBoardUsers(params = {}) {
  return await apiCall("get", "marketplace/fulfillment/board-users", null, params);
}

/**
 * Get the current user's assigned fulfillment tasks.
 * @param {Object} params - { status?, page?, limit? }
 */
async function getMyTasks(params = {}) {
  return await apiCall("get", "marketplace/fulfillment/my-tasks", null, params);
}

/**
 * Get tasks — admin sees all providers, others see only their own.
 * @param {Object} params - { serviceSlug?, status?, page?, limit? }
 * @param {boolean} isAdmin - if true, use all-tasks; otherwise use my-tasks
 */
async function getAllTasks(params = {}, isAdmin = true) {
  const endpoint = isAdmin
    ? "marketplace/fulfillment/all-tasks"
    : "marketplace/fulfillment/my-tasks";
  return await apiCall("get", endpoint, null, params);
}

/**
 * The cancelled archive's rows.
 *
 * Not a new endpoint — the same all-tasks/my-tasks pair every other list uses,
 * with `status: "cancelled"` pinned here so no caller can forget it and get a
 * live board back. That also means the provider scoping is the one already
 * proven on the board: my-tasks self-scopes to cards the caller worked, as the
 * assigned provider OR a non-CUSTOMER stage assignee.
 *
 * @param {Object} params - { page?, limit?, serviceSlug?, providerId?, search?, cancelledVia?, cancelledFrom?, cancelledTo? }
 * @param {boolean} isWideBoard - picks all-tasks (as wide as the caller's
 *   scope reaches) vs my-tasks (their own cards only)
 */
async function getCancelledTasks(params = {}, isWideBoard = true) {
  const endpoint = isWideBoard
    ? "marketplace/fulfillment/all-tasks"
    : "marketplace/fulfillment/my-tasks";
  return await apiCall("get", endpoint, null, {
    ...params,
    status: "cancelled",
  });
}

/**
 * The cancelled archive's summary band — totals, work lost, median lifetime,
 * and breakdowns by reason / service / canceller.
 *
 * Deliberately NOT part of `getFulfillmentCounts`: cancelled work is excluded
 * from every live tally on the board and stays that way. This is a separate
 * call for a separate screen, scoped server-side to the caller.
 *
 * @param {Object} params - same lenses as getCancelledTasks (no paging)
 */
async function getCancelledStats(params = {}) {
  return await apiCall(
    "get",
    "marketplace/fulfillment/cancelled-stats",
    null,
    params
  );
}

/**
 * What may be hand-created for a customer, and why not the rest.
 *
 * The SERVER owns this answer. Do not reconstruct it client-side from
 * `getUserEntitlements` + `getServicesList`: the cap is `remaining` (not
 * `totalPurchased − cards`, which reads already-delivered units as available),
 * at_claim services and delivery-disabled services are both refused by the
 * write path, and a `status: "live"` catalogue silently hides every retired
 * SKU's paid work.
 *
 * Returns `{ options: [{ serviceSlug, serviceName, available, totalPurchased,
 * consumed, remaining, committedCards, stages, blockedCode, blockedReason }] }`
 * — every quota row, creatable or not, so the UI can say WHY.
 */
async function getManualCreateOptions(gamePlayerId) {
  return await apiCall("get", "marketplace/fulfillment/create-options", null, {
    gamePlayerId,
  });
}

/**
 * Manually create a fulfillment task from an existing entitlement — a BACKFILL
 * door for delivery that happened outside the tracker. Never grants quota.
 * @param {Object} data - { gamePlayerId, serviceSlug, startingStageKey?, providerId?, dueDate?, workStartedAt?, notes? }
 *   `workStartedAt` anchors the whole SLA plan from when the work really began,
 *   so a backdated card is honestly overdue rather than born with a fresh
 *   deadline. Stages before `startingStageKey` are recorded as declared, not
 *   worked, and notify nobody.
 */
async function createManualFulfillment(data) {
  return await apiCall("post", "marketplace/fulfillment/create", data);
}

/**
 * Assign a provider to a fulfillment entry.
 * @param {Object} data - { gamePlayerId, serviceSlug, fulfillmentId, providerId, dueDate? }
 */
async function assignProvider(data) {
  return await apiCall("post", "marketplace/fulfillment/assign", data);
}

/**
 * Advance a fulfillment to the next stage.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug, stageKey, notes? }
 */
async function advanceStage(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/advance-stage`,
    data
  );
}

/**
 * Mark a fulfillment as completed.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug }
 */
async function completeFulfillment(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/complete`,
    data
  );
}

/**
 * Update the due date on a fulfillment entry.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug, dueDate }
 */
async function updateDueDate(fulfillmentId, data) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/${fulfillmentId}/due-date`,
    data
  );
}

/**
 * Get pending service provider requests (submitted form but not yet activated).
 */
async function getPendingProviderRequests() {
  return await apiCall(
    "get",
    "marketplace/fulfillment/providers/pending"
  );
}

/**
 * Approve or decline a "become a guide" application without granting anything.
 * Activation stays `updateProviderConfig` (it mints a staff role and carries the
 * internal-staff guard) and closes the request itself.
 * @param {string} gamePlayerId
 * @param {"approved"|"declined"} decision
 * @param {string} [decisionNote] internal only — never shown to the applicant
 */
async function decideProviderRequest(gamePlayerId, decision, decisionNote) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/providers/${gamePlayerId}/request`,
    { decision, ...(decisionNote ? { decisionNote } : {}) }
  );
}

/**
 * Get list of active providers.
 * @param {Object} params - { capability?, organizationType? }
 */
async function getProviders(params = {}) {
  return await apiCall(
    "get",
    "marketplace/fulfillment/providers",
    null,
    params
  );
}

/**
 * The providers ONE card may be reassigned to — the card-scoped twin of
 * `getProviders`.
 *
 * `getProviders` is the Team-tab roster and is gated on team administration, so
 * a SERVICE_PROVIDER handing off their OWN card cannot call it. This returns the
 * same rows (with the per-row `assignability` verdict the picker disables on)
 * behind the card's own read gate, so a provider can pick who to pass the card
 * to. `serviceSlug` names the capability to filter on.
 *
 * @param {string} fulfillmentId
 * @param {Object} params - { serviceSlug }
 */
async function getAssignableProviders(fulfillmentId, params = {}) {
  return await apiCall(
    "get",
    `marketplace/fulfillment/${fulfillmentId}/assignable-providers`,
    null,
    params
  );
}

/**
 * Services someone can be made responsible for — a provider's capabilities and
 * a manager's overseen services both pick from this.
 *
 * NOT the same as "services that require a provider". A service switched to
 * `requiresProvider: false` stops minting NEW cards but keeps the ones it
 * already made, and those still need somebody. The server unions both.
 */
async function getCapabilityOptions() {
  return await apiCall("get", "marketplace/fulfillment/capability-options");
}

/**
 * Get the admin-only Managers list: SERVICE_MANAGERs + ADMINs with the services
 * each oversees. Managers are not providers and are never manually assignable —
 * the only automatic assignment path is the overdue-task-SLA fallback.
 */
async function getManagers() {
  return await apiCall("get", "marketplace/fulfillment/managers");
}

/**
 * Get a single provider's job stats (completed count, active status, capabilities).
 * @param {string} providerId - Game Player ID
 */
async function getProviderWorkload(providerId) {
  return await apiCall(
    "get",
    `marketplace/fulfillment/providers/${providerId}/workload`
  );
}

/**
 * Update provider config (capabilities, active status).
 * @param {string} providerId - Game Player ID
 * @param {Object} data - { capabilities?, isActiveProvider?, organizationType?, partnerOrg? }
 */
async function updateProviderConfig(providerId, data) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/providers/${providerId}/config`,
    data
  );
}

/**
 * Take a user off the provider roster entirely — clears providerConfig, so they
 * leave the roster and stop being assignable at all. The twin of
 * `demoteManager`; 409s while they still hold in-flight cards.
 *
 * NOT the same as deactivating (`updateProviderConfig({isActiveProvider:false})`),
 * which keeps the config so they stay listed and reactivatable.
 * @param {string} providerId
 */
async function removeProvider(providerId) {
  return await apiCall(
    "delete",
    `marketplace/fulfillment/providers/${providerId}`
  );
}

/**
 * Set the services a manager oversees (admin-only Managers tab).
 * @param {string} gamePlayerId
 * @param {Object} data - { managedServices: string[] }
 */
async function updateManagerConfig(gamePlayerId, data) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/managers/${gamePlayerId}/config`,
    data
  );
}

/**
 * Promote a user to SERVICE_MANAGER with the services they oversee (admin-only).
 * @param {Object} data - { gamePlayerId, managedServices?: string[] }
 */
async function promoteManager(data) {
  return await apiCall("post", "marketplace/fulfillment/managers", data);
}

/**
 * Activate or deactivate a manager (admin-only). Deactivation keeps the role
 * and config but removes oversight + SLA-fallback eligibility.
 * @param {string} gamePlayerId
 * @param {boolean} isActive
 */
async function setManagerStatus(gamePlayerId, isActive) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/managers/${gamePlayerId}/status`,
    { isActive }
  );
}

/**
 * Remove a user's manager role entirely (admin-only) — clears managerConfig, so
 * they leave the Managers list, stop drawing SLA fallbacks, and become eligible
 * for the provider roster again. 409s while they still hold in-flight cards.
 * @param {string} gamePlayerId
 */
async function demoteManager(gamePlayerId) {
  return await apiCall(
    "delete",
    `marketplace/fulfillment/managers/${gamePlayerId}`
  );
}

/**
 * Search users by name/email.
 *
 * Uses the OpenSearch-backed /search/users endpoint rather than the Mongo
 * $text-based /game-player/profile/users. $text tokenizes punctuation and
 * drops stop words, which makes pasted-email queries ("shouvik@navigus.in")
 * rank poorly. OpenSearch lets us boost exact email matches explicitly.
 *
 * @param {Object} params - { search, limit?, skip?, includeApprovedGigProviders? }
 *   (Accepts `search` for backwards compat; maps to the OpenSearch `q` param.)
 *   `includeApprovedGigProviders` tells the server to not filter out
 *   existing marketplace sellers — needed for the "Add Provider" flow so
 *   known moderators / sellers can be activated as fulfillment providers.
 */
async function searchUsers(params = {}) {
  const { search, limit, skip, includeApprovedGigProviders } = params;
  return await apiCall("get", "search/users", null, {
    q: search,
    limit,
    skip,
    includeApprovedGigProviders: includeApprovedGigProviders ? true : undefined,
    // `search/users` is a PUBLIC endpoint with a strict projection allowlist —
    // requesting `email`/`providerConfig` (both disallowed, to prevent anonymous
    // PII exfiltration) made every call 400, so the picker returned nothing.
    // Request only allowlisted fields. Email-match ranking still works (it's a
    // query-side boost, not a projection); a searched user's provider config /
    // email are surfaced through the auth-gated fulfillment `providers` /
    // `providers/pending` endpoints, not this one.
    select: "gamePlayerId firstName lastName userName avatar isApprovedGigProvider",
  });
}

/**
 * Get every live service (for capabilities multi-select, fulfillment filters,
 * etc.). Returns services with slug, name, category, requiresProvider.
 *
 * Filters by status="live" — not isActive — because isActive only hides a
 * service from the pricing-page grid; live-but-inactive services are still
 * purchasable via Shopify, plan bundles, or direct embed and therefore still
 * need providers, queue rows, and capability mappings.
 */
async function getServicesList() {
  return await apiCall("get", "marketplace/services", null, {
    status: "live",
  });
}

/**
 * Search organizations by name.
 * @param {string} query - Search term
 */
async function searchOrganizations(query) {
  return await apiCall("get", "organization/search-by-name", null, {
    query,
    limit: 10,
  });
}

/**
 * Create a new organization.
 * @param {Object} data - { displayName, descriptionText?, websiteLink? }
 */
async function createOrganization(data) {
  return await apiCall("post", "organization", data);
}

/**
 * Restart a fulfillment: close this card and mint a fresh one for the same
 * credit in one write. Nothing is consumed and nothing is withdrawn.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug, reason? }
 */
async function restartFulfillment(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/restart`,
    data
  );
}

/**
 * Cancel a fulfillment AND withdraw the buyer's credit behind it — the
 * refund-shaped door. `reason` is required; `force` admits a card whose work
 * has already started (the server 409s without it).
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug, reason, force? }
 */
async function revokeFulfillmentCredit(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/revoke-credit`,
    data
  );
}

/**
 * Put an out-of-circulation at_claim credit back in the claimable pool.
 *
 * The counterpart of cancelling an unclaimed credit: cancel takes a paid unit
 * out of circulation, this puts one back. Addressed by (owner, service) rather
 * than by a fulfillmentId — the thing being returned is a CREDIT, which by
 * definition has no card at the moment you ask for it.
 *
 * Never a grant: `remaining` is untouched, the buyer already owns the unit.
 *
 * @param {Object} data - { gamePlayerId, serviceSlug }
 */
async function returnCreditToPool(data) {
  return await apiCall("post", "marketplace/fulfillment/credits/return", data);
}

/**
 * Put a fulfillment task on hold.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug, reason? }
 */
async function holdFulfillment(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/hold`,
    data
  );
}

/**
 * Resume a fulfillment task from on-hold state.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug }
 */
async function resumeFulfillment(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/resume`,
    data
  );
}

/**
 * Start delivery on a plan-granted, SLA-frozen task — un-freezes the clock.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug }
 */
async function startServiceDelivery(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/start-delivery`,
    data
  );
}

/**
 * Update a specific stage's status and/or due date.
 * Notes are NOT settable here — see updateStageNotes.
 * @param {string} fulfillmentId
 * @param {string} stageKey
 * @param {Object} data - { gamePlayerId, serviceSlug, status?, dueDate?, skipReason? }
 */
async function updateStage(fulfillmentId, stageKey, data) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/${fulfillmentId}/stages/${stageKey}`,
    data
  );
}

/**
 * Reassign a single stage to a different person + role.
 * Staff-only on the server. CUSTOMER role enforces the assignee is the
 * entitlement owner; the server rejects any other id paired with that role.
 * @param {string} fulfillmentId
 * @param {string} stageKey
 * @param {Object} data - { gamePlayerId, serviceSlug, assigneeId, assigneeRole }
 */
async function reassignStage(fulfillmentId, stageKey, data) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/${fulfillmentId}/stages/${stageKey}/assignee`,
    data
  );
}

/**
 * Update fulfillment-level notes.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug, notes }
 */
async function updateFulfillmentNotes(fulfillmentId, data) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/${fulfillmentId}/notes`,
    data
  );
}

/**
 * Update ONE stage's internal working note. Pass an empty `notes` to clear it.
 *
 * Internal-only: the server refuses a customer even on a stage assigned to
 * them, and never ships stage notes in the customer's own entitlements
 * payload. Don't render these on a customer-facing surface.
 * @param {string} fulfillmentId
 * @param {string} stageKey
 * @param {Object} data - { gamePlayerId, serviceSlug, notes }
 */
async function updateStageNotes(fulfillmentId, stageKey, data) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/${fulfillmentId}/stages/${stageKey}/notes`,
    data
  );
}

/**
 * Reassign a fulfillment to a different provider.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug, providerId }
 */
/**
 * "This one is mine" — a manager keeps a card the fallback handed them.
 *
 * Flips `assignedVia` from `manager_fallback` to `manager_fixed`, which is the
 * ONLY way a card leaves the fallback: nothing automatic may move it after
 * this, and the retry sweep stops looking at it. Deliberately a button rather
 * than an inference — the stage engine advances stages AS support, who is also
 * the last-resort assignee, so "the assignee started a stage" is true of every
 * signal-driven card with no human anywhere near it.
 *
 * Server CASes on the caller BEING the current fallback holder, so only offer
 * it to them.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug }
 */
async function keepFallbackCard(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/keep`,
    data
  );
}

async function reassignProvider(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/reassign`,
    data
  );
}

/**
 * Get activity timeline for a fulfillment entry.
 */
async function getTimeline(fulfillmentId, params = {}) {
  return await apiCall(
    "get",
    `marketplace/fulfillment/${fulfillmentId}/timeline`,
    null,
    params
  );
}

/**
 * Add a comment to a fulfillment timeline.
 */
async function addComment(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/comments`,
    data
  );
}

/**
 * Soft-delete a comment.
 */
async function deleteComment(fulfillmentId, commentId) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/${fulfillmentId}/comments/${commentId}`
  );
}

/**
 * Search mentionable users for @mentions in comments.
 */
async function searchMentionableUsers(query) {
  return await apiCall(
    "get",
    "marketplace/fulfillment/mentions/search",
    null,
    { q: query, limit: 5 }
  );
}

/**
 * Watch a fulfillment task — current user receives activity notifications.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug }
 */
async function watchFulfillment(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/watch`,
    data
  );
}

/**
 * Stop watching a fulfillment task.
 * @param {string} fulfillmentId
 * @param {Object} data - { gamePlayerId, serviceSlug }
 */
async function unwatchFulfillment(fulfillmentId, data) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/${fulfillmentId}/unwatch`,
    data
  );
}

// ── Assignment policy APIs ──

/**
 * List all services with a summary of their auto-assignment policy.
 * One row per service — used in the Teams → Assignment Rules tab.
 */
async function listAssignmentPolicies() {
  return await apiCall("get", "marketplace/fulfillment/assignment-policies");
}

/**
 * Get the full assignment policy for a service slug.
 */
async function getAssignmentPolicy(slug) {
  return await apiCall(
    "get",
    `marketplace/fulfillment/assignment-policies/${slug}`
  );
}

/**
 * Replace the service's auto-assignment policy.
 * @param {string} slug
 * @param {Object} policy - { enabled, strategies, retry? }
 */
async function updateAssignmentPolicy(slug, policy) {
  return await apiCall(
    "patch",
    `marketplace/fulfillment/assignment-policies/${slug}`,
    policy
  );
}

/**
 * Dry-run the policy against the current provider pool without assigning.
 * Pass `policy` to simulate an unsaved policy from the builder; omit to
 * simulate against the currently-saved policy.
 * Returns `{ result: { providerId, providerName, trace }, pool: [...],
 * excluded: [...], policySource }`. `excluded` is every roster member holding
 * the capability who is NOT a candidate, with the reason — the answer to "why
 * didn't it pick who I expected?", which the pool alone cannot give.
 */
async function dryRunAssignmentPolicy(slug, policy, buyerFacts) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/assignment-policies/${slug}/dry-run`,
    {
      ...(policy ? { policy } : {}),
      ...(buyerFacts ? { buyerFacts } : {}),
    }
  );
}

/**
 * Trigger a retry pass over every card this service's fallback is holding.
 * Useful after toggling the policy on for the first time.
 */
async function retryFallbackForService(slug) {
  return await apiCall(
    "post",
    `marketplace/fulfillment/assignment-policies/${slug}/retry-fallback`
  );
}

export {
  getFulfillmentCounts,
  getDeliveryMe,
  getCalendarCounts,
  getDateTasks,
  getBoard,
  getTaskById,
  getUsersOverview,
  getBoardUsers,
  getMyTasks,
  getAllTasks,
  getCancelledTasks,
  getCancelledStats,
  createManualFulfillment,
  getManualCreateOptions,
  returnCreditToPool,
  assignProvider,
  advanceStage,
  completeFulfillment,
  restartFulfillment,
  revokeFulfillmentCredit,
  holdFulfillment,
  resumeFulfillment,
  startServiceDelivery,
  updateDueDate,
  updateStage,
  updateStageNotes,
  reassignStage,
  updateFulfillmentNotes,
  reassignProvider,
  getPendingProviderRequests,
  decideProviderRequest,
  getProviders,
  getAssignableProviders,
  getCapabilityOptions,
  getManagers,
  getProviderWorkload,
  keepFallbackCard,
  updateProviderConfig,
  removeProvider,
  updateManagerConfig,
  promoteManager,
  setManagerStatus,
  demoteManager,
  searchUsers,
  getServicesList,
  searchOrganizations,
  createOrganization,
  getTimeline,
  addComment,
  deleteComment,
  searchMentionableUsers,
  watchFulfillment,
  unwatchFulfillment,
  listAssignmentPolicies,
  getAssignmentPolicy,
  updateAssignmentPolicy,
  dryRunAssignmentPolicy,
  retryFallbackForService,
};
